import { useCallback, useEffect } from 'react';

interface UseVideoKeyInputsProps {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  onPause: () => void;
  onPlay: () => Promise<void>;
  onToggleMute: () => void;
  setCurrentTime: (time: number) => void;
  toggleFullscreen: () => Promise<void>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

export const useVideoKeyInputs = ({
  videoRef,
  currentTime,
  duration,
  isPlaying,
  onPause,
  onPlay,
  setCurrentTime,
  toggleFullscreen,
  onToggleMute,
}: UseVideoKeyInputsProps) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const videoPlayer = videoRef.current;

      if (!videoPlayer) {
        return;
      }

      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const hasModifier =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

      let newTime: number;

      switch (event.key.toLowerCase()) {
        case ' ': // Spacebar
        case 'k':
        case 'mediaplaypause':
          if (hasModifier) {
            break;
          }
          event.preventDefault();

          if (isPlaying) {
            onPause();
          } else {
            void onPlay();
          }

          break;

        case 'm':
          if (hasModifier) {
            break;
          }
          event.preventDefault();
          onToggleMute();
          break;

        case 'f':
          if (hasModifier) {
            break;
          }
          event.preventDefault();
          void toggleFullscreen();
          break;

        case 'j':
          if (hasModifier) {
            break;
          }
          event.preventDefault();
          newTime = Math.max(0, currentTime - 5);
          videoPlayer.currentTime = newTime;
          setCurrentTime(newTime);
          break;

        case 'l':
          if (hasModifier) {
            break;
          }
          event.preventDefault();
          newTime = Math.min(duration, currentTime + 5);
          videoPlayer.currentTime = newTime;
          setCurrentTime(newTime);
          break;

        case ',':
          if (hasModifier) {
            break;
          }
          if (!isPlaying) {
            event.preventDefault();
            newTime = Math.max(0, currentTime - 0.1);
            videoPlayer.currentTime = newTime;
            setCurrentTime(newTime);
          }

          break;

        case '.':
          if (hasModifier) {
            break;
          }
          if (!isPlaying) {
            event.preventDefault();
            newTime = Math.min(duration, currentTime + 0.1);
            videoPlayer.currentTime = newTime;
            setCurrentTime(newTime);
          }

          break;

        case 'arrowleft':
          event.preventDefault();
          newTime = Math.max(0, currentTime - 1);
          videoPlayer.currentTime = newTime;
          setCurrentTime(newTime);
          break;

        case 'arrowright':
          event.preventDefault();
          newTime = Math.min(duration, currentTime + 1);
          videoPlayer.currentTime = newTime;
          setCurrentTime(newTime);
          break;

        default:
          break;
      }
    },
    [
      currentTime,
      duration,
      isPlaying,
      onPause,
      onPlay,
      onToggleMute,
      setCurrentTime,
      toggleFullscreen,
      videoRef,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};
